import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  User,
  Package,
  LogOut,
  MapPin,
  ChevronRight,
  Plus,
  Trash2,
  Truck,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { getStoredUser, logout, isAuthenticated } from "../api/authApi";
import { getOrders } from "../api/orderApi";
import { getAddresses, addAddress, removeAddress } from "../api/userApi";
import { toast } from "../hooks/use-toast";
import AddressForm from "../components/AddressForm";

const getDays = (endDate) => {
  if (!endDate) return null;
  return Math.ceil((new Date(endDate) - new Date()) / 86400000);
};

const fmtDate = (date) => {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const Profile = () => {
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [addressLoading, setAddressLoading] = useState(false);
  const [isAddAddressOpen, setIsAddAddressOpen] = useState(false);
  const [newAddress, setNewAddress] = useState({
    fullName: "",
    phone: "",
    pincode: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    country: "India",
    addressType: "Home",
  });

  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is authenticated
    if (!isAuthenticated()) {
      navigate("/login");
      return;
    }

    // Get user from localStorage
    const storedUser = getStoredUser();
    if (storedUser) {
      setUser(storedUser);
    } else {
      navigate("/login");
    }
  }, [navigate]);

  const fetchAddresses = async () => {
    try {
      setAddressLoading(true);
      const response = await getAddresses();
      if (response.success) {
        setAddresses(response.data);
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
    } finally {
      setAddressLoading(false);
    }
  };

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setOrdersLoading(true);
        const response = await getOrders();
        if (response.success && response.data?.orders) {
          setOrders(response.data.orders);
        }
      } catch (error) {
        console.error("Error fetching orders:", error);
        toast({
          title: "Error",
          description: error.response?.data?.error || "Failed to load orders",
          variant: "destructive",
        });
      } finally {
        setOrdersLoading(false);
      }
    };

    if (user) {
      fetchOrders();
      fetchAddresses();
    }
  }, [user]);

  const handleAddAddress = async (e) => {
    e.preventDefault();
    try {
      const response = await addAddress(newAddress);
      if (response.success) {
        setAddresses(response.data);
        setIsAddAddressOpen(false);
        setNewAddress({
          fullName: "",
          phone: "",
          pincode: "",
          addressLine1: "",
          addressLine2: "",
          city: "",
          state: "",
          country: "India",
          addressType: "Home",
        });
        toast({ title: "Success", description: "Address added successfully" });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add address",
        variant: "destructive",
      });
    }
  };

  const handleDeleteAddress = async (id) => {
    try {
      const response = await removeAddress(id);
      if (response.success) {
        setAddresses(response.data);
        toast({ title: "Success", description: "Address removed" });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove address",
        variant: "destructive",
      });
    }
  };

  const handleLogout = () => {
    logout();
    toast({
      title: "Logged Out",
      description: "You have been successfully logged out.",
    });
    navigate("/login");
  };

  if (!user) {
    return null; // or a loading spinner
  }

  // Helper function to format order ID
  const formatOrderId = (orderId) => {
    // If it's a MongoDB ObjectId, format it nicely
    if (orderId && orderId.length === 24) {
      return `ORD-${orderId.slice(-6).toUpperCase()}`;
    }
    return orderId || "N/A";
  };

  // Helper function to get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case "DELIVERED":
      case "APPROVED":
        return "bg-green-500";
      case "SHIPPED":
      case "IN_TRANSIT":
        return "bg-blue-500";
      case "PENDING":
        return "bg-orange-500";
      case "CANCELLED":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  // Helper function to format status display
  const formatStatus = (status) => {
    return status.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <div className="py-12 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold mb-8 dark:text-white">Profile</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* User Info Card */}
          <div className="lg:col-span-1">
            <Card>
              <CardContent className="p-6">
                <div className="flex flex-col items-center text-center mb-6">
                  <div className="w-32 h-32 bg-black dark:bg-gray-800 border dark:border-gray-700 rounded-full flex items-center justify-center mb-4">
                    <User className="w-16 h-16 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold mb-2 dark:text-white">{user.name}</h2>
                </div>

                <Separator className="my-4" />

                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Email</p>
                    <p className="font-medium dark:text-white">{user.email}</p>
                  </div>
                  {user.phone && (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Phone</p>
                      <p className="font-medium dark:text-white">
                        <span className="mr-2">🇮🇳</span>
                        {user.phone}
                      </p>
                    </div>
                  )}
                  {user.role && (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Role</p>
                      <Badge className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                        {user.role.replace("_", " ")}
                      </Badge>
                    </div>
                  )}
                  {user.companyName && (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Company</p>
                      <p className="font-medium dark:text-white">{user.companyName}</p>
                    </div>
                  )}
                </div>

                <Separator className="my-6" />

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleLogout}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Orders and Addresses */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="orders" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="orders">My Orders</TabsTrigger>
                <TabsTrigger value="warranty-renewal">
                  Warranty Renewal
                </TabsTrigger>
                <TabsTrigger value="addresses">Addresses</TabsTrigger>
              </TabsList>

              <TabsContent value="orders" className="mt-6">
                {ordersLoading ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <p className="text-gray-600 dark:text-gray-400">Loading orders...</p>
                    </CardContent>
                  </Card>
                ) : orders.length === 0 ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <div className="mb-6">
                        <Package className="w-24 h-24 mx-auto text-gray-300 dark:text-gray-600" />
                      </div>
                      <h3 className="text-xl font-bold mb-2 dark:text-white">No orders yet.</h3>
                      <p className="text-gray-600 dark:text-gray-400 mb-6">
                        Start shopping to see your orders here
                      </p>
                      <Link to="/all-products">
                        <Button className="bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-black">
                          Start Shopping
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => {
                      const isRenewal = order.orderType === "WARRANTY_RENEWAL";
                      const orderId = order._id || order.id;

                      // ── WARRANTY_RENEWAL card
                      if (isRenewal) {
                        const productName =
                          order.renewalMeta?.productName || "Product";
                        const renewalMonths =
                          order.renewalMeta?.renewalDuration;
                        return (
                          <Link key={orderId} to={`/order/${orderId}`}>
                            <Card className="hover:shadow-lg transition cursor-pointer border-blue-100 dark:border-blue-900/50">
                              <CardContent className="p-6">
                                <div className="flex items-start justify-between mb-4">
                                  <div>
                                    <div className="flex items-center space-x-3 mb-2">
                                      <h3 className="font-bold text-lg dark:text-white">
                                        {formatOrderId(orderId)}
                                      </h3>
                                      <Badge className="bg-blue-600 text-white text-xs">
                                        Renewal Warranty
                                      </Badge>
                                      <Badge
                                        className={`${getStatusColor(order.status)} text-white`}
                                      >
                                        {formatStatus(order.status)}
                                      </Badge>
                                    </div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                      Renewed on{" "}
                                      {new Date(
                                        order.createdAt || order.date,
                                      ).toLocaleDateString("en-IN", {
                                        day: "numeric",
                                        month: "short",
                                        year: "numeric",
                                      })}
                                    </p>
                                  </div>
                                  <ChevronRight className="w-5 h-5 text-gray-400" />
                                </div>
                                <div className="flex items-center space-x-4 mb-4">
                                  <div className="w-20 h-20 rounded bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                                    <span style={{ fontSize: 36 }}>🛡️</span>
                                  </div>
                                  <div className="flex-1">
                                    <h4 className="font-semibold line-clamp-1 dark:text-white">
                                      {productName}
                                    </h4>
                                    {renewalMonths ? (
                                      <p className="text-sm text-gray-600 dark:text-gray-400">
                                        {renewalMonths} Month Extension
                                      </p>
                                    ) : null}
                                    {order.renewalMeta?.newWarrantyEndDate && (
                                      <p className="text-xs text-green-700 dark:text-green-400 font-medium mt-1">
                                        New Expiry:{" "}
                                        {fmtDate(
                                          order.renewalMeta.newWarrantyEndDate,
                                        )}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <Separator className="my-4" />
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                      Amount Paid
                                    </p>
                                    <p className="text-xl font-bold dark:text-white">
                                      ₹
                                      {order.totalAmount?.toLocaleString() ||
                                        "0"}
                                    </p>
                                  </div>
                                  <Button variant="outline">
                                    View Details
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          </Link>
                        );
                      }

                      // ── Regular B2C / B2B card
                      const firstProduct = order.products?.[0]?.productId;
                      const productId = firstProduct?._id || firstProduct?.id;
                      const productName = firstProduct?.name || "Product";
                      let productImage = "/placeholder-image.jpg";
                      if (
                        firstProduct?.images &&
                        Array.isArray(firstProduct.images) &&
                        firstProduct.images.length > 0
                      ) {
                        productImage = firstProduct.images[0];
                      } else if (firstProduct?.image) {
                        productImage = firstProduct.image;
                      }
                      const quantity = order.products?.[0]?.quantity || 0;
                      // Track Order button shown ONLY when order is SHIPPED
                      const isShipped = order.status === "SHIPPED";
                      return (
                        <Card
                          key={orderId}
                          className="hover:shadow-lg transition cursor-pointer"
                          onClick={() => navigate(`/order/${orderId}`)}
                        >
                          <CardContent className="p-6">
                            <div className="flex items-start justify-between mb-4">
                              <div>
                                <div className="flex items-center space-x-3 mb-2">
                                  <h3 className="font-bold text-lg dark:text-white">
                                    {formatOrderId(orderId)}
                                  </h3>
                                  <Badge
                                    className={`${getStatusColor(order.status)} text-white`}
                                  >
                                    {formatStatus(order.status)}
                                  </Badge>
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  Ordered on{" "}
                                  {new Date(
                                    order.createdAt || order.date,
                                  ).toLocaleDateString("en-IN", {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                  })}
                                </p>
                              </div>
                              <ChevronRight className="w-5 h-5 text-gray-400" />
                            </div>
                            <div className="flex items-center space-x-4 mb-4">
                              <img
                                src={productImage}
                                alt={productName}
                                className="w-20 h-20 object-cover rounded"
                                onError={(e) => {
                                  if (
                                    e.target.src !==
                                    window.location.origin +
                                      "/placeholder-image.jpg"
                                  ) {
                                    e.target.src = "/placeholder-image.jpg";
                                  }
                                }}
                                loading="lazy"
                              />
                              <div className="flex-1">
                                <h4 className="font-semibold line-clamp-1 dark:text-white">
                                  {productName}
                                </h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  Quantity: {quantity}
                                </p>
                                {order.orderType === "B2B" && (
                                  <Badge className="bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 text-xs mt-1">
                                    B2B Order
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <Separator className="my-4" />
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  Total Amount
                                </p>
                                <p className="text-xl font-bold dark:text-white">
                                  ₹
                                  {order.totalAmount?.toLocaleString() || "0"}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                {/* Track Order button — shown for ALL orders */}
                                <Button
                                  variant="outline"
                                  className="flex items-center gap-1.5 border-blue-200 text-blue-600 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/20"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    navigate(`/track-order/${orderId}`);
                                  }}
                                >
                                  <Truck size={14} />
                                  Track Order
                                </Button>
                                <Button
                                  variant="outline"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    navigate(`/order/${orderId}`);
                                  }}
                                >
                                  View Details
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              {/* Warranty Renewal Tab */}
              <TabsContent value="warranty-renewal" className="mt-6">
                {ordersLoading ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <p className="text-gray-600 dark:text-gray-400">Loading...</p>
                    </CardContent>
                  </Card>
                ) : orders.length === 0 ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <div className="mb-4">
                        <span style={{ fontSize: 60 }}>🛡️</span>
                      </div>
                      <h3 className="text-xl font-bold mb-2 dark:text-white">
                        No products to renew.
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        Products with expiring warranties will appear here.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {orders.flatMap((order) =>
                      (order.products || [])
                        .filter((item) => {
                          // Only show products where admin has defined warranty renewal plans
                          const renewalOpts =
                            item?.productId?.warrantyRenewalOptions || [];
                          return renewalOpts.length > 0;
                        })
                        .map((item) => {
                          const product = item.productId;
                          if (!product) return null;
                          const productId = product._id || product.id;
                          const orderId = order._id || order.id;
                          const productName = product.name || "Product";
                          let productImage = "/placeholder-image.jpg";
                          if (product.images && product.images.length > 0) {
                            productImage = product.images[0];
                          }

                          const warrantyEnd = item.warrantyEndDate;
                          const warrantyStart =
                            item.warrantyStartDate || order.createdAt;
                          const days = getDays(warrantyEnd);
                          const isExpired = days !== null && days < 0;
                          const isWarnSoon =
                            days !== null && days >= 0 && days <= 30;

                          const totalDays =
                            warrantyStart && warrantyEnd
                              ? Math.ceil(
                                  (new Date(warrantyEnd) -
                                    new Date(warrantyStart)) /
                                    86400000,
                                )
                              : 0;
                          const used = totalDays - (days || 0);
                          const pct =
                            totalDays > 0
                              ? Math.min(
                                  100,
                                  Math.max(0, (used / totalDays) * 100),
                                )
                              : 0;

                          return (
                            <Card
                              key={`${orderId}-${productId}`}
                              className="hover:shadow-md transition overflow-hidden border border-gray-200 dark:border-gray-700 flex flex-col"
                            >
                              <CardContent className="p-4 pb-4">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                  {/* Left: product image + name */}
                                  <div className="flex items-center gap-4 flex-1 min-w-0">
                                    <img
                                      src={productImage}
                                      alt={productName}
                                      className="w-16 h-16 object-cover rounded-lg flex-shrink-0 border border-gray-100 dark:border-gray-700"
                                      onError={(e) => {
                                        e.target.src = "/placeholder-image.jpg";
                                      }}
                                      loading="lazy"
                                    />
                                    <div className="min-w-0">
                                      <h4 className="font-semibold text-sm leading-tight line-clamp-2 text-gray-900 dark:text-white">
                                        {productName}
                                      </h4>
                                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                          Order:{" "}
                                          <span className="font-medium text-gray-700 dark:text-gray-300">
                                            {formatOrderId(orderId)}
                                          </span>
                                        </p>
                                        {warrantyEnd && (
                                          <>
                                            <span className="hidden sm:inline-block w-1 h-1 bg-gray-300 dark:bg-gray-600 rounded-full"></span>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                              Expires:{" "}
                                              <span
                                                className={`font-medium ${isExpired ? "text-red-500" : isWarnSoon ? "text-orange-500" : "text-gray-700 dark:text-gray-300"}`}
                                              >
                                                {fmtDate(warrantyEnd)}
                                              </span>
                                            </p>
                                          </>
                                        )}
                                      </div>
                                      {days !== null && (
                                        <p
                                          className={`text-xs font-bold mt-1.5 ${isExpired ? "text-red-500" : isWarnSoon ? "text-orange-500" : "text-green-600"}`}
                                        >
                                          {isExpired
                                            ? "Warranty Expired"
                                            : `${days} Days Remaining`}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  {/* Right: Renew Now button */}
                                  <button
                                    onClick={() =>
                                      navigate(
                                        `/warranty-renewal/${orderId}/${productId}`,
                                      )
                                    }
                                    className="flex-shrink-0 w-full sm:w-auto px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold rounded-xl transition-colors shadow-sm"
                                  >
                                    Renew Now
                                  </button>
                                </div>
                              </CardContent>
                              {/* Bottom Progress Bar */}
                              {totalDays > 0 && (
                                <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-700 flex-shrink-0 mt-auto">
                                  <div
                                    className={`h-full transition-all duration-500 ${isExpired ? "bg-red-500" : isWarnSoon ? "bg-orange-400" : "bg-green-500"}`}
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                              )}
                            </Card>
                          );
                        })
                        .filter(Boolean),
                    )}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="addresses" className="mt-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold dark:text-white">Saved Addresses</h3>
                  <Dialog
                    open={isAddAddressOpen}
                    onOpenChange={setIsAddAddressOpen}
                  >
                    <DialogTrigger asChild>
                      <Button className="flex items-center gap-2">
                        <Plus size={16} /> Add New Address
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Add New Address</DialogTitle>
                        <DialogDescription>
                          Enter detailed address information for delivery.
                        </DialogDescription>
                      </DialogHeader>
                      <AddressForm
                        address={newAddress}
                        onChange={setNewAddress}
                        onSubmit={handleAddAddress}
                        onCancel={() => {
                          setIsAddAddressOpen(false);
                          setNewAddress({
                            fullName: "",
                            phone: "",
                            addressLine1: "",
                            addressLine2: "",
                            city: "",
                            state: "",
                            pincode: "",
                            country: "India",
                            addressType: "Home",
                          });
                        }}
                        submitLabel="Save Address"
                      />
                    </DialogContent>
                  </Dialog>
                </div>

                <div className="space-y-4">
                  {addressLoading ? (
                    <p className="dark:text-gray-300">Loading addresses...</p>
                  ) : addresses.length === 0 ? (
                    <Card>
                      <CardContent className="p-12 text-center">
                        <div className="mb-6">
                          <MapPin className="w-24 h-24 mx-auto text-gray-300 dark:text-gray-600" />
                        </div>
                        <h3 className="text-xl font-bold mb-2 dark:text-white">
                          No addresses found.
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                          Add a new address to speed up checkout
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    <>
                      {addresses.map((address) => (
                        <Card
                          key={address._id || address.id}
                          className={
                            address.isDefault
                              ? "border-2 border-black dark:border-white relative"
                              : "relative"
                          }
                        >
                          <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-2">
                                  <h3 className="font-bold dark:text-white">
                                    {address.fullName}
                                  </h3>
                                  <Badge variant="outline">
                                    {address.addressType}
                                  </Badge>
                                  {address.isDefault && (
                                    <Badge className="bg-black dark:bg-white text-white dark:text-black">
                                      Default
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-gray-700 dark:text-gray-300 mb-1">
                                  {address.addressLine1}
                                </p>
                                {address.addressLine2 && (
                                  <p className="text-gray-700 dark:text-gray-300 mb-1">
                                    {address.addressLine2}
                                  </p>
                                )}
                                <p className="text-gray-700 dark:text-gray-300 mb-1">
                                  {address.city}, {address.state}
                                </p>
                                <p className="text-gray-700 dark:text-gray-300 mb-2">
                                  {address.pincode}
                                </p>
                                {address.country && (
                                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-1">
                                    Country: {address.country}
                                  </p>
                                )}
                                <p className="text-gray-600 dark:text-gray-400 text-sm">
                                  Phone: {address.phone}
                                </p>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteAddress(address._id)}
                              >
                                <Trash2 size={18} />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
